import type { MoneyModel } from 'shared/ui/FormatMoney';
import { formatPrice } from 'shared/lib/formatting';
import { IconWithTooltip } from 'shared/ui/Tooltip';

import type { PendingChangesAttributesModel, PendingChangesModel } from '../model';

const labels = {
  zipCodes: 'Zip codes',
  budget: 'Budget',
  pause: 'Pause campaign',
  running: 'Run campaign',
  schedule: 'Lead receiving time',
  minimalIncome: 'Minimal income',
  callCenterSchedule: 'Call center schedule',
};

function Placeholder({ isDeactivated, appliesAt }: { isDeactivated: boolean; appliesAt: string }) {
  return isDeactivated ? (
    <>
      <div>The following changes will be applied</div>
      <div>after campaign activation but not</div>
      <div>earlier than the following {appliesAt}:</div>
    </>
  ) : (
    <>
      <div>The following changes will be applied</div>
      <div>on {appliesAt}:</div>
    </>
  );
}

interface MinimalIncomeItemProps {
  name: keyof typeof labels;
  oldPrice: string | number;
  newPrice: string | number;
}

function MinimalIncomeItem({ name, oldPrice, newPrice }: MinimalIncomeItemProps) {
  return (
    <span className="whitespace-nowrap">
      {labels[name]}:{' '}
      <span className="font-medium">
        {formatPrice(oldPrice)} {'->'} {formatPrice(newPrice)}
      </span>
    </span>
  );
}

function PendingTooltipItem({
  item,
  minimalIncome,
}: {
  item: PendingChangesAttributesModel;
  minimalIncome?: MoneyModel;
}) {
  if (minimalIncome && item.name === 'minimalIncome' && item.value) {
    return (
      <MinimalIncomeItem
        oldPrice={item.value}
        newPrice={minimalIncome.amount}
        name={item.name}
      />
    );
  }

  return <>{labels[item.name] || item.name}</>;
}

export function PendingTooltip({
  appliesAt,
  attributes,
  className,
  isDeactivated,
  minimalIncome,
}: PendingChangesModel & {
  className?: string;
  isDeactivated: boolean;
  minimalIncome?: MoneyModel;
}) {
  return (
    <IconWithTooltip
      icon="update"
      className={className}
      iconClassNames="text-lg text-[16px] text-orange-500 cursor-pointer"
      tooltip={
        <div className="w-[250px] p-3 text-left text-xs">
          <Placeholder
            appliesAt={appliesAt}
            isDeactivated={isDeactivated}
          />
          {attributes.map((item, i) => (
            <div
              key={item.name}
              className="list-disc"
            >
              <span className="ml-[6px] mr-[5px]">•</span>
              <PendingTooltipItem
                item={item}
                minimalIncome={minimalIncome}
              />
              {i === attributes.length - 1 ? '.' : ';'}
            </div>
          ))}
        </div>
      }
    />
  );
}
